/**
 * Who should cover a window, and why.
 *
 * The database reports facts per guard (`roster_fix_candidate_guards`); this
 * module owns the judgement. Blockers are things that would break the law or
 * double-book someone, so those guards are never suggested. Everyone else is
 * scored so the suggestion an admin sees first is the one they would pick.
 *
 * The rule checks here are only a forecast — the dry-run preview is what
 * decides whether a suggestion is lawful.
 */

import { windowMinutes } from './rosterModel';
import type {
  CandidateBlockerCode,
  CandidateGuard,
  CandidateHighlightCode,
  CandidateNote,
  RankedCandidate,
  RosterRule,
  TimeWindow,
} from './types';

export interface RankingContext {
  window: TimeWindow;
  rule: RosterRule | null;
}

/** Score weights, kept together so the trade-offs are visible in one place. */
export const CANDIDATE_SCORE = {
  base: 100,
  preferredWindow: 15,
  declaredAvailable: 8,
  /** Per previous shift at this site in the last 90 days, capped. */
  perSiteShift: 1.5,
  siteShiftCap: 10,
  /** Per hour already rostered this week: spreads load across the team. */
  perWeekHour: -1,
  /** Joining onto an existing shift makes a long day. */
  extendsShift: -10,
  /** Per hour of rest beyond the legal minimum, capped. */
  perSpareRestHour: 0.5,
  spareRestCapHours: 12,
} as const;

function assessBlockers(guard: CandidateGuard, context: RankingContext): CandidateNote<CandidateBlockerCode>[] {
  const { rule } = context;
  const blockers: CandidateNote<CandidateBlockerCode>[] = [];
  const coverMinutes = windowMinutes(context.window);

  if (!guard.hasRequiredLicence) blockers.push({ code: 'no_licence' });
  if (guard.onApprovedLeave) blockers.push({ code: 'on_leave' });
  if (guard.availability === 'unavailable') blockers.push({ code: 'unavailable' });
  if (guard.overlappingShiftSummary) blockers.push({ code: 'overlap', text: guard.overlappingShiftSummary });

  const minimumRest = rule?.minimumRestMinutes ?? null;
  if (minimumRest !== null && minimumRest > 0) {
    if (guard.restBeforeMinutes !== null && guard.restBeforeMinutes < minimumRest) {
      blockers.push({ code: 'short_rest_before', value: guard.restBeforeMinutes });
    }
    if (guard.restAfterMinutes !== null && guard.restAfterMinutes < minimumRest) {
      blockers.push({ code: 'short_rest_after', value: guard.restAfterMinutes });
    }
  }

  const maxDays = rule?.maxConsecutiveDays ? Math.floor(rule.maxConsecutiveDays) : null;
  if (maxDays && guard.consecutiveWorkDays > maxDays) {
    blockers.push({ code: 'consecutive_days', value: guard.consecutiveWorkDays });
  }

  if (rule?.maxAverageWeeklyMinutes) {
    const weeks = Math.max(rule.weeklyReferenceWeeks ?? 1, 1);
    const projectedAverage = (guard.referenceMinutes + coverMinutes) / weeks;
    if (projectedAverage > rule.maxAverageWeeklyMinutes) {
      blockers.push({ code: 'weekly_average', value: Math.round(projectedAverage) });
    }
  }

  if (rule?.maxShiftMinutes && guard.mergedShiftMinutes + coverMinutes > rule.maxShiftMinutes) {
    blockers.push({ code: 'shift_too_long', value: guard.mergedShiftMinutes + coverMinutes });
  }

  return blockers;
}

function assessHighlights(guard: CandidateGuard): CandidateNote<CandidateHighlightCode>[] {
  const highlights: CandidateNote<CandidateHighlightCode>[] = [];
  if (guard.availability === 'preferred') highlights.push({ code: 'preferred' });
  if (guard.availability === 'available') highlights.push({ code: 'available' });
  if (guard.siteShiftCount > 0) highlights.push({ code: 'site_familiar', value: guard.siteShiftCount });
  highlights.push({ code: 'week_hours', value: guard.weekMinutes });
  if (guard.restBeforeMinutes !== null) highlights.push({ code: 'rest_before', value: guard.restBeforeMinutes });
  if (guard.restAfterMinutes !== null) highlights.push({ code: 'rest_after', value: guard.restAfterMinutes });
  if (guard.mergedShiftMinutes > 0) highlights.push({ code: 'extends_shift', value: guard.mergedShiftMinutes });
  return highlights;
}

function spareRestHours(restMinutes: number | null, minimumRest: number): number {
  if (restMinutes === null) return CANDIDATE_SCORE.spareRestCapHours;
  return Math.min(Math.max(restMinutes - minimumRest, 0) / 60, CANDIDATE_SCORE.spareRestCapHours);
}

function score(guard: CandidateGuard, rule: RosterRule | null): number {
  const minimumRest = rule?.minimumRestMinutes ?? 0;
  let total: number = CANDIDATE_SCORE.base;
  if (guard.availability === 'preferred') total += CANDIDATE_SCORE.preferredWindow;
  if (guard.availability === 'available') total += CANDIDATE_SCORE.declaredAvailable;
  total += Math.min(guard.siteShiftCount, CANDIDATE_SCORE.siteShiftCap) * CANDIDATE_SCORE.perSiteShift;
  total += (guard.weekMinutes / 60) * CANDIDATE_SCORE.perWeekHour;
  if (guard.mergedShiftMinutes > 0) total += CANDIDATE_SCORE.extendsShift;
  total += Math.min(
    spareRestHours(guard.restBeforeMinutes, minimumRest),
    spareRestHours(guard.restAfterMinutes, minimumRest),
  ) * CANDIDATE_SCORE.perSpareRestHour;
  return Math.round(total * 10) / 10;
}

export function assessCandidate(guard: CandidateGuard, context: RankingContext): RankedCandidate {
  const blockers = assessBlockers(guard, context);
  return {
    guard,
    eligible: blockers.length === 0,
    score: score(guard, context.rule),
    blockers,
    highlights: assessHighlights(guard),
  };
}

/** Eligible guards first, best score first; ties fall back to name for a stable order. */
export function rankCandidates(guards: CandidateGuard[], context: RankingContext): RankedCandidate[] {
  return guards
    .map((guard) => assessCandidate(guard, context))
    .sort((a, b) => (
      Number(b.eligible) - Number(a.eligible)
      || b.score - a.score
      || a.guard.fullName.localeCompare(b.guard.fullName)
    ));
}

/**
 * Merges per-licence lookups for one window: a guard qualifies only if every
 * lookup found them licensed. Other facts do not depend on the licence.
 */
export function mergeLicenceLookups(lookups: CandidateGuard[][]): CandidateGuard[] {
  if (lookups.length === 0) return [];
  const [first, ...rest] = lookups;
  const licensedElsewhere = rest.map((lookup) => new Map(lookup.map((guard) => [guard.guardId, guard.hasRequiredLicence])));
  return first.map((guard) => ({
    ...guard,
    hasRequiredLicence: guard.hasRequiredLicence
      && licensedElsewhere.every((lookup) => lookup.get(guard.guardId) === true),
  }));
}
